import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { helper } from '@ember/component/helper';
import { TrackedObject } from 'tracked-built-ins';

interface SampleData {
  opAmpPartUrl: string;
  opAmpPart: string;
  opAmpPartModels: { ModelPackage: string; ModelNumber: string; Generic: string; [x: string]: any }[];
  hasModelsWithSamples: boolean;
  samplesLink: string;
  modelNumber: string;
}

export interface AditoolsDialogSamplesSignature {
  Element: HTMLDialogElement;
  Args: {
    siteURL?: string;
    locale?: string;
    shoppingCartBaseUrl?: string;
    title?: string;
    dialogEnabled?: boolean;
    message?: string;
    samplesModel: { [x: string]: any }[];
    filter?: (arg0: unknown) => boolean;
    close: () => void;
    order: (model: string) => void;
    dialogClass?: string;
  };
}

const modelOrderUrlHelperFn = function (baseUrl: string, adiLocale: string, model: { [x: string]: any }) {
  const modelNumber = model['modelNumber'] as string;
  return baseUrl.replace('{partlist}', modelNumber).replace('{adi}', adiLocale);
};

const modelOrderUrlHelper = function (positional: Array<any>) {
  return modelOrderUrlHelperFn(positional[0] as string, positional[1] as string, positional[2]);
};

export default class AditoolsDialogSamples extends Component<AditoolsDialogSamplesSignature> {
  @tracked samplesData: SampleData[] = this._buildSamplesData(this._samplesModel);
  modelOrderUrl = helper(modelOrderUrlHelper);
  get _title() {
    return this.args.title || '';
  }
  get _siteURL() {
    return this.args.siteURL || 'https://www.analog.com';
  }

  get _locale() {
    return this.args.locale ?? 'en';
  }

  get _shoppingCartBaseUrl() {
    return this.args.shoppingCartBaseUrl ?? '';
  }

  get _samplesModel() {
    return this.args.samplesModel ?? [];
  }

  _buildSamplesData(samplesModel: { [x: string]: any }[]) {
    const samplesData: SampleData[] = [];
    for (const sample of samplesModel) {
      const models: SampleData['opAmpPartModels'] = [];
      let currModelDesc = '',
        hasModelsWithSamples = true,
        partGeneric = '',
        modelNumber = '';
      if (sample['Models']) {
        partGeneric = sample['Generic'];
        for (const model of sample['Models']) {
          const item = model;
          // sort model parameters by Parameter ID value
          item.ModelParameters.sort(function (a: { ParameterID: string }, b: { ParameterID: string }) {
            return parseInt(a.ParameterID) - parseInt(b.ParameterID);
          });

          for (let n = 0; n < item.ModelParameters.length; n++) {
            const modelParam = item.ModelParameters[n];

            if (modelParam.ParameterID === 3) {
              // ParameterID 3 is 'Package_Descr'
              currModelDesc =
                item.ModelNumber.toString().replace(item.Generic, '') +
                ' - ' +
                modelParam.ParameterValue.toString().toUpperCase();
            }
            if (modelParam.ParameterID === 997) {
              // ParameterID 997 is 'Send Sample'
              if (
                modelParam.ParameterValue === '94' ||
                modelParam.ParameterValue === 'D' ||
                modelParam.ParameterValue === 'Y'
              ) {
                item.ModelPackage = currModelDesc.trim();
                if (!this.args.filter || !this.args.filter(item)) {
                  if (!modelNumber) modelNumber = item.ModelNumber;
                  models.push(item);
                }
              }
            }
          }
          currModelDesc = '';
        }
      } else {
        if (sample['errorMessage'].length > 0) {
          partGeneric = sample['errorMessage'].errorMessage
            .substring(sample['errorMessage'].lastIndexOf('/') + 1, sample['errorMessage'].lastIndexOf('.js'))
            .toUpperCase();
        }
      }
      if (models.length < 1) {
        models.push({ ModelPackage: 'No Samples Available', ModelNumber: '', Generic: '' });
        hasModelsWithSamples = false;
      }

      const sampleURLString =
        this.args.siteURL + '/' + this.args.locale + '/' + partGeneric + '.html#product-samplebuy';

      const samplesLink = this._buildSamplesLink(modelNumber);

      samplesData.push(
        new TrackedObject({
          opAmpPart: partGeneric,
          opAmpPartModels: models,
          opAmpPartUrl: sampleURLString,
          hasModelsWithSamples: hasModelsWithSamples,
          samplesLink: samplesLink,
          modelNumber: modelNumber,
        })
      );
    }
    return samplesData;
  }

  _buildSamplesLink(modelNumber: string) {
    const adiLocale = this.args.locale ?? 'en';
    const cdpLocale = this.args.locale ? (this.args.locale === 'cn' ? 'zh' : this.args.locale) : 'en';
    const isoLocale = this.args.locale
      ? this.args.locale === 'cn'
        ? 'zh'
        : this.args.locale === 'jp'
        ? 'ja'
        : this.args.locale
      : 'en';

    if (this.args.shoppingCartBaseUrl) {
      return this.args.shoppingCartBaseUrl
        .replace('{partlist}', modelNumber)
        .replace('{adi}', adiLocale)
        .replace('{cdp}', cdpLocale)
        .replace('{iso}', isoLocale);
    }
    return '';
  }

  @action updateSelectedModel(model: { [x: string]: any }, e: Event) {
    const target = e.target as HTMLSelectElement;
    model['modelNumber'] = target?.value;
  }

  @action processSamples(model: string) {
    this.args.order(model);
  }

  //The following is called after the dialog closes
  @action closeSamples() {
    // Reset the selected value for each dropdown to the first one in the list
    for (const sample of this.samplesData) {
      if (sample.opAmpPartModels[0]) {
        sample.modelNumber = sample.opAmpPartModels[0].ModelNumber;
      }
    }
    this.args.close();
  }
}
